import React from 'react';
import { Alert, AlertTitle, Button, Box, Snackbar, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppSelector } from 'src/store';
import useAuth from 'src/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

const BillingBanner: React.FC = () => {
  const theme = useTheme();
  const { isLoggedIn } = useAuth();
  const { user } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Reset banner when billing status changes
  useEffect(() => {
    setBannerDismissed(false);
  }, [user?.billing?.past_due, user?.billing?.has_payment_method, user?.billing?.requires_payment_method]);

  // Don't show banner if user is not logged in or no user data
  if (!isLoggedIn || !user || !user.billing) {
    return null;
  }

  // Don't show banner on billing pages
  const billingPaths = [
    '/billing-and-plans',
    '/choose-your-plan',
    '/pricing',
    '/subscription/success',
    '/checkout/cancel'
  ];

  const isOnBillingPage = billingPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  if (isOnBillingPage) {
    return null;
  }

  // Check billing status
  const isPastDue = user.billing.past_due;
  const needsPaymentMethod = !user.billing.has_payment_method && user.billing.requires_payment_method;

  // Don't show banner if everything is okay or banner was dismissed
  if ((!isPastDue && !needsPaymentMethod) || bannerDismissed) {
    return null;
  }

  const handleUpdatePayment = () => {
    if (isPastDue) {
      navigate('/billing-and-plans');
    } else {
      navigate('/choose-your-plan');
    }
  };

  const handleClose = () => {
    setBannerDismissed(true);
  };

  // Determine severity and message based on billing status
  let severity: 'error' | 'warning' = 'warning';
  let title = 'Payment Method Required';
  let message = 'Please add a payment method to continue using all features.';
  let icon = <WarningAmberIcon />;

  if (isPastDue) {
    severity = 'error';
    title = 'Account Past Due';
    message = 'Your account is past due. Please update your payment method immediately to avoid service interruption.';
    icon = <ErrorOutlineIcon />;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.snackbar + 1,
        padding: 0,
      }}
    >
      <Alert
        severity={severity}
        icon={icon}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleUpdatePayment}
              sx={{
                borderColor: 'currentColor',
                '&:hover': {
                  borderColor: 'currentColor',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              Update Payment
            </Button>
            {!isPastDue && (
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
        sx={{
          borderRadius: 0,
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
          },
          '& .MuiAlert-message': {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>{title}</AlertTitle>
        {message}
      </Alert>
    </Box>
  );
};

export default BillingBanner;